import React, { useCallback, useEffect, useId, useState } from 'react';
import {
  CircleCheck,
  CircleDollarSign,
  CircleHelp,
  ChevronDown,
  Download,
  FileText,
  Info,
  TriangleAlert,
  Users,
  Wallet,
} from 'lucide-react';
import Modal from 'v3/components/Modal';
import Button from 'v3/components/Button';
import { formatDate } from 'v3/utils/controlTowerDate';
import { formatOccurrenceMoney, occurrenceHint, validSummary } from './contract';
import useOccurrences from './useOccurrences';
import useOccurrenceAttachments from './useOccurrenceAttachments';
import './styles.css';

const missing = 'Não informado';

function Attachments({ occurrenceId, count }) {
  const { loading, data, error } = useOccurrenceAttachments(occurrenceId, count > 0);
  return (
    <section className="tc-occ-box" aria-label="Anexos da ocorrência">
      <h4>
        <Download size={14} aria-hidden="true" /> Anexos ({count})
      </h4>
      {count === 0 && <p>Sem anexos.</p>}
      {loading && <p role="status">Carregando anexos…</p>}
      {error && <p role="alert">{error}</p>}
      {data && (
        <ul className="tc-occ-attachments">
          {data.map((file) => (
            <li key={file.id}>
              <a href={file.url} target="_blank" rel="noopener noreferrer" download>
                <Download size={14} aria-hidden="true" /> {file.name}
              </a>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Occurrence({ row }) {
  const [open, setOpen] = useState(false);
  const detailsId = useId();
  const Icon = row.pending ? TriangleAlert : CircleCheck;
  const created = [row.createdBy, row.createdAt && formatDate(row.createdAt)]
    .filter(Boolean)
    .join(' ');
  return (
    <li className={`tc-occ-card${open ? ' tc-occ-open' : ''}`} data-occurrence-id={row.id}>
      <button
        type="button"
        className="tc-occ-summary-row"
        aria-expanded={open}
        aria-controls={detailsId}
        onClick={() => setOpen(!open)}
      >
        <span className={`tc-occ-icon ${row.pending ? 'tc-occ-icon-pending' : 'tc-occ-icon-done'}`}>
          <Icon size={18} aria-hidden="true" />
        </span>
        <span className="tc-occ-title">
          <strong>{row.type || 'Tipo não informado'}</strong>
          <small>
            Inclusão: <b>{created || missing}</b>
          </small>
        </span>
        <span
          className={`tc-occ-badge ${row.pending ? 'tc-occ-badge-pending' : 'tc-occ-badge-done'}`}
        >
          {row.pending ? 'Pendente' : 'Concluída'}
        </span>
        <span className="tc-occ-number">
          Ocorrência: <b>{row.id}</b>
        </span>
        <ChevronDown size={18} aria-hidden="true" className="tc-occ-chevron" />
      </button>
      {open && (
        <div id={detailsId} className="tc-occ-details">
          <dl className="tc-occ-grid">
            <div>
              <dt>Nº ocorrência cliente</dt>
              <dd>{row.customerNumber || missing}</dd>
            </div>
            <div>
              <dt>Início ocorrência</dt>
              <dd>{formatDate(row.startedAt)}</dd>
            </div>
            <div>
              <dt>Término ocorrência</dt>
              <dd>{formatDate(row.endedAt)}</dd>
            </div>
            <div className="tc-occ-with-icon">
              <Users size={16} aria-hidden="true" />
              <div>
                <dt>Grupo responsável</dt>
                <dd>{row.groups.length ? row.groups.join(', ') : missing}</dd>
              </div>
            </div>
          </dl>
          <dl className="tc-occ-grid tc-occ-values">
            <div className="tc-occ-with-icon">
              <CircleDollarSign size={16} aria-hidden="true" className="tc-occ-pay" />
              <div>
                <dt>A pagar ao transportador</dt>
                <dd>{formatOccurrenceMoney(row.payCarrier)}</dd>
              </div>
            </div>
            <div className="tc-occ-with-icon">
              <CircleDollarSign size={16} aria-hidden="true" className="tc-occ-receive" />
              <div>
                <dt>A receber do cliente</dt>
                <dd>{formatOccurrenceMoney(row.receiverClient)}</dd>
              </div>
            </div>
            <div className="tc-occ-with-icon">
              <Wallet size={16} aria-hidden="true" className="tc-occ-retained" />
              <div>
                <dt>Retido filial</dt>
                <dd>{formatOccurrenceMoney(row.retainedBranch)}</dd>
              </div>
            </div>
          </dl>
          <div className="tc-occ-boxes">
            <section className="tc-occ-box" aria-label="Descrição da ocorrência">
              <h4>
                <FileText size={14} aria-hidden="true" /> Descrição
              </h4>
              <p className="tc-occ-description">{row.description || 'Descrição não informada'}</p>
              {row.concludedBy && <small>Concluída por: {row.concludedBy}</small>}
            </section>
            <Attachments occurrenceId={row.id} count={row.attachmentCount} />
          </div>
        </div>
      )}
    </li>
  );
}

function OccurrencesModal({ companyId, travelId, travelNumber, onClose, onSummary }) {
  const titleId = useId();
  const [page, setPage] = useState(1);
  const [revision, setRevision] = useState(0);
  const { loading, data, error } = useOccurrences(companyId, travelId, page, revision);
  useEffect(() => {
    if (data) onSummary(data.summary);
  }, [data, onSummary]);
  const refresh = () => setRevision((value) => value + 1);
  return (
    <Modal
      show
      centered
      handleClose={onClose}
      size="lg"
      scrollable
      className="tc-occ-modal"
      aria-labelledby={titleId}
      heading={
        <span id={titleId}>
          Ocorrências da Viagem: <span className="tc-occ-travel">{travelNumber || travelId}</span>
        </span>
      }
      body={
        <div className="tc-occ-body" aria-busy={loading}>
          <div className="tc-occ-toolbar">
            {data ? (
              <p className="tc-occ-totals" role="status">
                <span>Total de ocorrências: {data.summary.totalCount}</span>
                <span>Pendentes: {data.summary.pendingCount}</span>
                <span>Concluídas: {data.summary.completedCount}</span>
              </p>
            ) : (
              <span />
            )}
            <Button size="sm" variant="secondary" disabled={loading} onClick={refresh}>
              Atualizar ocorrências
            </Button>
          </div>
          {loading && <p role="status">Carregando ocorrências…</p>}
          {error && (
            <div role="alert" className="tc-occ-error">
              <p>{error}</p>
              <Button size="sm" onClick={refresh}>
                Tentar novamente
              </Button>
            </div>
          )}
          {data && (
            <>
              <h3 className="tc-occ-section">Ocorrências</h3>
              {data.data.length === 0 && (
                <p>
                  {data.summary.totalCount
                    ? 'Nenhuma ocorrência nesta página.'
                    : 'Sem ocorrências nesta viagem.'}
                </p>
              )}
              <ul className="tc-occ-list">
                {data.data.map((row) => (
                  <Occurrence key={row.id} row={row} />
                ))}
              </ul>
              <div className="tc-occ-pagination">
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                  aria-label="Página anterior de ocorrências"
                >
                  Anterior
                </Button>
                <span>
                  Página {page} de {data.pagination.totalPages || 1}
                </span>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={!data.pagination.hasMore}
                  onClick={() => setPage(page + 1)}
                  aria-label="Próxima página de ocorrências"
                >
                  Próxima
                </Button>
              </div>
              <small>Horários de Brasília · Consulta: {formatDate(data.asOf)}</small>
            </>
          )}
        </div>
      }
      footer={
        <Button size="sm" variant="secondary" onClick={onClose}>
          Fechar
        </Button>
      }
    />
  );
}

/** Reutilizável em qualquer lista que forneça identidade da viagem e resumo do servidor. */
export default function Occurrences({ item, context }) {
  const scope = `${context.companyId}:${item.travelId}`;
  const [opened, setOpened] = useState(null);
  const [observed, setObserved] = useState(null);
  const source = item.occurrenceSummary;
  const onSummary = useCallback(
    (summary) => setObserved({ scope, source, summary }),
    [scope, source],
  );
  const summary =
    observed?.scope === scope && observed.source === source ? observed.summary : source;
  const known = validSummary(summary);
  const hint = occurrenceHint(summary);
  const Icon = known ? Info : CircleHelp;
  return (
    <>
      <button
        type="button"
        className={`tc-trip-icon tc-occ-indicator ${known && summary.state === 'PENDING' ? 'tc-occ-pending' : 'tc-occ-completed'}`}
        title={hint}
        aria-label={`${hint} — viagem ${item.travelNumber || item.travelId}`}
        aria-haspopup="dialog"
        onClick={() => setOpened(scope)}
      >
        <Icon size={16} aria-hidden="true" />
      </button>
      {opened === scope && (
        <OccurrencesModal
          key={scope}
          companyId={context.companyId}
          travelId={item.travelId}
          travelNumber={item.travelNumber}
          onClose={() => setOpened(null)}
          onSummary={onSummary}
        />
      )}
    </>
  );
}
